import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../services/data.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {RestService} from '../services/rest.service';
import {finalize, tap} from 'rxjs/operators';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {Subscription} from 'rxjs';
import {MapInfoWindow, MapMarker} from '@angular/google-maps';

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.css']
})
export class MapComponent implements OnInit, OnDestroy {

    @ViewChild('carInfoWindow', {read: MapInfoWindow, static: false}) carInfoWindow: MapInfoWindow;

    carIds: string[] = [];
    filteredCarIds: string[] = [];
    selectedCarId = '';
    carSearchText = '';
    isCarDropdownOpen = false;
    resultOfSelection = '';
    counter = 0;
    timerId: any;
    carsOnMap: any;
    carsData: Array<any> = [];
    isLoadingMap = false;
    isLoadingCars = false;
    selectedOrder: any;
    selectedMapMarker: Marker;
    private readonly defaultLatitude = 44.429021;
    private readonly defaultLongitude = 26.103849;
    mapCenter: google.maps.LatLngLiteral = {lat: this.defaultLatitude, lng: this.defaultLongitude};
    firstTimeMapLoads = true;
    private readonly defaultZoom = 13;
    zoom = this.defaultZoom;
    private subscriptions = new Subscription();

    // moving path
    // 44.443634, 26.060500 - 1st
    // 44.444132, 26.060629 - 2nd
    // 44.445526, 26.060447 - 3rd

    // moving path
    // 44.429021, 26.103849 -1st
    // 44.432152, 26.103624 -2nd
    // 44.435339, 26.102756 -3rd

    // set some fake markers for now
    markers: Marker[] = [];

    constructor(private restService: RestService,
                private uiBlockerService: UIBlockerService,
                private dataService: DataService) { /* noop */
    }

    ngOnInit() {
        this.carsOnMap = this.dataService.getCurrentCarsOnMap();
        this.subscriptions.add(this.dataService.dataChangedCarsOnMap.subscribe(() => {
            this.carsOnMap = this.dataService.getCurrentCarsOnMap();
            this.parseReceivedCars();
        }));

        this.setCarsData(this.dataService.getCars());
        this.subscriptions.add(this.dataService.dataChangedCars.subscribe(() => {
            this.setCarsData(this.dataService.getCars());
        }));

        this.subscriptions.add(this.dataService.dataChangedSession.subscribe(() => {
            this.getCarsOnMapFromServer();
            this.getCarsData();
        }));

        if (!isNullOrUndefined(this.dataService.getSessionIdentifier())) {
            this.getCarsOnMapFromServer();
            this.getCarsData();
        }

        this.setTimer();
    }

    ngOnDestroy() {
        this.stopTimer();
        this.subscriptions.unsubscribe();
    }

    setCarsData(cars: Array<any>) {
        this.carsData = Array.isArray(cars) ? cars : [];
        this.carIds = Array.from(new Set(
            this.carsData
                .map((car) => isNullOrUndefined(car.CarId) ? '' : String(car.CarId).trim())
                .filter((carId) => carId !== '')
        )).sort((left, right) => left.localeCompare(right, undefined, {numeric: true}));
        this.filterCarIds(this.carSearchText);
    }

    onCarSearchChanged(value: string) {
        this.carSearchText = value || '';
        this.selectedCarId = '';
        this.resultOfSelection = '';
        this.filterCarIds(this.carSearchText);
        this.isCarDropdownOpen = true;

        if (this.carSearchText === '') {
            this.resetMapPosition();
        }
    }

    toggleCarDropdown() {
        if (this.isLoadingCars) {
            return;
        }
        this.filterCarIds(this.carSearchText);
        this.isCarDropdownOpen = !this.isCarDropdownOpen;
    }

    openCarDropdown() {
        if (!this.isLoadingCars) {
            this.filterCarIds(this.carSearchText);
            this.isCarDropdownOpen = true;
        }
    }

    closeCarDropdown() {
        window.setTimeout(() => {
            this.isCarDropdownOpen = false;
        }, 0);
    }

    selectFirstFilteredCar(event: KeyboardEvent) {
        event.preventDefault();
        if (this.filteredCarIds.length > 0) {
            this.selectCar(this.filteredCarIds[0]);
        }
    }

    selectCar(carId: string) {
        this.selectedCarId = carId || '';
        this.carSearchText = this.selectedCarId;
        this.isCarDropdownOpen = false;
        if (this.selectedCarId === '') {
            this.resetCarSelection();
            return;
        }

        const carExistsOnMap = this.markers.find((marker) => marker.label === this.selectedCarId);
        if (isNullOrUndefined(carExistsOnMap)) {
            this.resultOfSelection = `Car ${this.selectedCarId} is not currently visible on the map.`;
            this.zoom = this.defaultZoom;
        } else {
            this.resultOfSelection = '';
            this.setMapCenter(carExistsOnMap.lat, carExistsOnMap.lng);
            this.zoom = 15;
        }
    }

    resetCarSelection() {
        this.selectedCarId = '';
        this.carSearchText = '';
        this.filterCarIds('');
        this.isCarDropdownOpen = false;
        this.resultOfSelection = '';
        this.resetMapPosition();
    }

    getCarsOnMapFromServer() {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier()) || this.isLoadingMap) {
            return;
        }

        this.isLoadingMap = true;
        this.restService.getCarsOnMap()
            .pipe(
                finalize(() => {
                    this.isLoadingMap = false;
                    this.uiBlockerService.unBlockUI();
                }),
                tap(
                    () => {
                        /* noop */
                    },
                    () => {
                        /* noop */
                    }
                )
            )
            .subscribe((res: any) => {
                this.dataService.setCurrentCarsOnMap(res);
            });
    }

    clickedMarker(marker: Marker, mapMarker: MapMarker) {
        this.selectedMapMarker = marker;
        this.carInfoWindow.open(mapMarker);

        if (!marker.isBusy || marker.isLoadingOrder || !marker.carIdentifier) {
            return;
        }

        marker.isLoadingOrder = true;
        marker.orderLookupComplete = false;
        marker.activeOrder = undefined;

        this.restService.getActiveOrder(marker.carIdentifier)
            .pipe(finalize(() => {
                marker.isLoadingOrder = false;
                marker.orderLookupComplete = true;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe(
                (response: any) => {
                    marker.activeOrder = this.dataService.isResponseSuccessful(response)
                        ? response.CompleteOrder
                        : undefined;
                },
                () => {
                    marker.activeOrder = undefined;
                }
            );
    }

    openOrderDetails(order: any, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.selectedOrder = order;
    }

    closeOrderDetails() {
        this.selectedOrder = undefined;
    }

    getMarkerTitle(marker: Marker): string {
        const company = String(marker.companyIdentifier || '').trim().toUpperCase();
        return [company, marker.carId].filter((part) => part !== '').join(' ');
    }

    getOrderIdentifier(orderWrapper: any): string {
        if (isNullOrUndefined(orderWrapper)) {
            return '';
        }
        const order = orderWrapper.Order || orderWrapper;
        return isNullOrUndefined(order.OrderIdentifier) ? '' : String(order.OrderIdentifier);
    }

    setTimer() {
        this.timerId = setInterval(() => {
            this.getCarsOnMapFromServer();
        }, 5000); // every 5 seconds
    }

    stopTimer() {
        clearInterval(this.timerId);
    }

    getCarsData() {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier()) || this.isLoadingCars) {
            return;
        }

        this.isLoadingCars = true;
        this.restService.getCars()
            .pipe(finalize(() => {
                this.isLoadingCars = false;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe((response: any) => {
                if (this.dataService.isResponseSuccessful(response)) {
                    this.dataService.setCars(response.Cars);
                }
            });
    }

    parseReceivedCars() {
        if (!isNullOrUndefined(this.carsOnMap)) {
            // we must update the markers array with the correct values
            // we need to update current markers not replace entirely
            let positionOfLastCarLat = 0;
            let positionOfLastCarLng = 0;
            if (!isNullOrUndefined(this.carsOnMap.CarUpdates)) {
                // add the cars from this list to the map
                this.carsOnMap.CarUpdates.forEach((element) => {
                    const carId = isNullOrUndefined(element.CarId) ? '' : String(element.CarId);
                    const carData = this.carsData.find((car) => String(car.CarId) === carId);
                    const companyIdentifier = String(
                        element.CompanyIdentifier
                        || (!isNullOrUndefined(carData) && (carData.CompanyIdentifier || carData.CompanyName))
                        || ''
                    );
                    const carIdentifier = String(
                        element.CarIdentifier
                        || (!isNullOrUndefined(carData) && carData.CarIdentifier)
                        || companyIdentifier + carId
                    );
                    let iconUrlToShow = 'assets/images/mapCarGreen.png';
                    if (element.IsBusy) {
                        iconUrlToShow = 'assets/images/mapCarRed.png';
                    }
                    const markerToPush: Marker = {
                        lat: element.CarCoords.GpsLat,
                        lng: element.CarCoords.GpsLong,
                        label: carId,
                        carId,
                        companyIdentifier,
                        carIdentifier,
                        isBusy: Boolean(element.IsBusy),
                        position: {lat: element.CarCoords.GpsLat, lng: element.CarCoords.GpsLong},
                        labelOptions: {
                            color: '#ffffff',
                            fontSize: '1rem',
                            text: carId
                        },
                        options: {
                            draggable: false,
                            icon: iconUrlToShow
                        }
                    };

                    // we need to update in current markes array
                    const markerExits = this.markers.find((x) =>
                        carIdentifier !== '' ? x.carIdentifier === carIdentifier : x.label === markerToPush.label);
                    if (!isNullOrUndefined(markerExits)) {
                        markerExits.lat = markerToPush.lat;
                        markerExits.lng = markerToPush.lng;
                        markerExits.label = markerToPush.label;
                        markerExits.carId = markerToPush.carId;
                        markerExits.companyIdentifier = markerToPush.companyIdentifier;
                        markerExits.carIdentifier = markerToPush.carIdentifier;
                        markerExits.isBusy = markerToPush.isBusy;
                        markerExits.position = markerToPush.position;
                        markerExits.labelOptions = markerToPush.labelOptions;
                        markerExits.options = markerToPush.options;
                        if (!markerExits.isBusy) {
                            markerExits.activeOrder = undefined;
                            markerExits.isLoadingOrder = false;
                            markerExits.orderLookupComplete = false;
                        }
                    } else {
                        // new car so add to array
                        this.markers.push(markerToPush);
                    }

                    positionOfLastCarLat = element.CarCoords.GpsLat;
                    positionOfLastCarLng = element.CarCoords.GpsLong;
                });

                if (positionOfLastCarLat !== 0 && positionOfLastCarLng !== 0 && this.firstTimeMapLoads) {
                    // use this position to center on map
                    this.setMapCenter(positionOfLastCarLat, positionOfLastCarLng);
                    this.zoom = 13;
                    this.firstTimeMapLoads = false;
                }
            }
            // need to remove car only if appears in CarRemovals - that means we should keep old cars because they

            // might not move
            if (!isNullOrUndefined(this.carsOnMap.CarRemovals)) {
                // need to remove items from this array that are found in markers
                this.carsOnMap.CarRemovals.forEach((element) => {
                    // if it is found in current markers remove
                    const markerExits = this.markers.find((x) =>
                        element.CarIdentifier
                            ? x.carIdentifier === element.CarIdentifier
                            : x.label === String(element.CarId));
                    if (!isNullOrUndefined(markerExits)) {
                        // remove
                        const index = this.markers.indexOf(markerExits);
                        if (index > -1) {
                            this.markers.splice(index, 1);
                        }
                    }
                });
            }

            if (this.selectedCarId !== '') {
                const selectedMarker = this.markers.find((marker) => marker.label === this.selectedCarId);
                if (!isNullOrUndefined(selectedMarker)) {
                    this.resultOfSelection = '';
                    this.setMapCenter(selectedMarker.lat, selectedMarker.lng);
                } else {
                    this.resultOfSelection = `Car ${this.selectedCarId} is not currently visible on the map.`;
                }
            }
        }
    }

    private resetMapPosition() {
        this.setMapCenter(this.defaultLatitude, this.defaultLongitude);
        this.zoom = this.defaultZoom;
    }

    trackByItems(index: number, car: Marker): string {
        Boolean(index);
        return car.carIdentifier || car.label;
    }

    private filterCarIds(search: string) {
        const normalizedSearch = String(search || '').trim().toLowerCase();
        this.filteredCarIds = normalizedSearch === ''
            ? this.carIds.slice()
            : this.carIds.filter((carId) => carId.toLowerCase().indexOf(normalizedSearch) >= 0);
    }

    // Get Current Location Coordinates
    private setCurrentLocation() {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition((position) => {
                this.setMapCenter(position.coords.latitude, position.coords.longitude);
                this.zoom = 13;
            });
        }
    }

    private setMapCenter(lat: number, lng: number) {
        this.mapCenter = {lat, lng};
    }

}

// just an interface for type safety.
interface Marker {
    lat: number;
    lng: number;
    label: string;
    carId: string;
    companyIdentifier: string;
    carIdentifier: string;
    isBusy: boolean;
    position: google.maps.LatLngLiteral;
    labelOptions: google.maps.MarkerLabel;
    options: google.maps.MarkerOptions;
    activeOrder?: any;
    isLoadingOrder?: boolean;
    orderLookupComplete?: boolean;
}
